import React, { useEffect } from 'react'
import {
	CopyrightAgentLoader,
	DangerButton,
	PrimaryButton,
	Row,
	SearchInput,
	Typography,
} from '@copyrightagent/basic-components'
import globalUseStyle from '../style'
import { UploadImagesModal } from './UploadImagesModal'
import InfiniteScroll from 'react-infinite-scroll-component'
import { blue } from '../../../../lib/Colors'
import { useRegistrationCertificatesStore } from '../stores/registrationCertificateStore'
import { useRegisteredImagesStore } from '../stores/registeredImagesStore'
import { toast } from '../../../../store/utils'
import { RouteComponentProps } from 'react-router-dom'
import {
	deleteRegistrationCertificate,
	getRegistrationCertificateFileLink,
} from '../api' // Your parent store

export const RegistrationCertificateDetails = ({
	match,
	history,
}: RouteComponentProps<{ registrationCertificateId: string }>) => {
	const { registrationCertificateId } = match.params

	const {
		removeRegistrationCertificate,
		incrementRegistrationCertificateImageCount,
	} = useRegistrationCertificatesStore()
	const globalClasses = globalUseStyle()

	const {
		registeredImages,
		totalRegisteredImagesCount,
		searchString,
		fetchRegisteredImages,
		selectedRegistrationCertificate,
		deleteImageId,
		setSearchString,
		setUploadImagesModalOpen,
	} = useRegisteredImagesStore()

	useEffect(() => {
		const fetchRegisteredImagesPromise = async () => {
			const response = await fetchRegisteredImages(registrationCertificateId, {
				isInitialLoad: true,
			})
			if (!response.isSuccessful) {
				await toast('Failed to fetch registration certificate details', 'error')
			}
		}
		fetchRegisteredImagesPromise()
	}, [registrationCertificateId])

	const handleSearch = () => {
		fetchRegisteredImages(registrationCertificateId, {
			isInitialLoad: true,
			searchString,
		})
	}

	const handleLoadMore = () => {
		fetchRegisteredImages(registrationCertificateId)
	}

	const handleDeleteImage = async (registeredImageId: string) => {
		const response = await deleteImageId({
			registeredImageId,
			registrationCertificateId,
		})
		if (response?.isSuccessful) {
			incrementRegistrationCertificateImageCount({
				registrationCertificateId,
				incrementImageCountBy: -1,
			})
			await toast('Successfully deleted Image', 'success')
		} else {
			await toast(
				'Something went wrong when deleting image. Please try again later.',
				'error',
			)
		}
	}

	const viewCertificate = async () => {
		const response = await getRegistrationCertificateFileLink({
			registrationCertificateId,
		})
		if (!response.isSuccessful) {
			return await toast(
				'File link generation failed. Please try again later.',
				'error',
			)
		}
		console.log(response.data)
		const url = response.data.url

		window.open(url, '_blank')
	}

	const handleDeleteRegistrationCertificate = async ({
		registrationCertificateId,
	}: {
		registrationCertificateId: string
	}) => {
		const response = await deleteRegistrationCertificate({
			registrationCertificateId,
		})
		if (response.isSuccessful) {
			history.push('/registration-certificates')
			removeRegistrationCertificate(registrationCertificateId)
			await toast('Registration certificate deleted successfully', 'success')
		} else {
			await toast(
				'Failed to delete registration certificate. Please try again later.',
				'error',
			)
		}
	}

	return (
		<div className={globalClasses.subPageContainer}>
			{selectedRegistrationCertificate ? (
				<>
					<UploadImagesModal />
					<div className={globalClasses.titleContainer}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
							<div>
								<Typography variant="body2" className={globalClasses.listTitle}>
									{
										selectedRegistrationCertificate.registration_certificate_number
									}
								</Typography>
								<Typography variant="body2" fontColor="rgb(142, 147, 155)">
									Effective Date:{' '}
									{selectedRegistrationCertificate.effective_registration_date}
								</Typography>
								{selectedRegistrationCertificate.gcs_uri ? (
									<button
										style={{ border: 'none', cursor: 'pointer' }}
										onClick={viewCertificate}
									>
										<Typography fontColor={blue} fontWeight={600}>
											Click here to view certificate
										</Typography>
									</button>
								) : (
									<Typography variant="body2" fontColor="rgb(142, 147, 155)">
										No certificate uploaded
									</Typography>
								)}
							</div>
						</div>
						<DangerButton
							text={'Delete Certificate'}
							onClick={() =>
								handleDeleteRegistrationCertificate({
									registrationCertificateId:
										selectedRegistrationCertificate.registration_certificate_id,
								})
							}
						/>
					</div>

					<div
						className={globalClasses.titleContainer}
						style={{ marginBottom: 0 }}
					>
						<div>
							<Typography variant="body2" className={globalClasses.listTitle}>
								Images
							</Typography>
							<Typography
								variant="body2"
								fontColor="rgb(142, 147, 155)"
								style={{ marginBottom: '16px' }}
							>
								{totalRegisteredImagesCount} Images
							</Typography>
						</div>
						<PrimaryButton
							text={'Upload Images'}
							onClick={() => setUploadImagesModalOpen(true)}
						/>
					</div>

					<div className={globalClasses.searchContainer}>
						<SearchInput
							onChange={(e) => setSearchString(e.target.value)}
							placeholder={'Search for Images'}
							value={searchString}
						/>
						<PrimaryButton
							style={{ marginLeft: '16px' }}
							text={'Search'}
							onClick={handleSearch}
						/>
					</div>
					<div
						id="partnerImagesList"
						className={globalClasses.listContainer}
						style={{
							height: 'calc(100vh - 370px)',
						}}
					>
						{registeredImages.length > 0 ? (
							<InfiniteScroll
								className={globalClasses.infiniteScrollWrap}
								dataLength={registeredImages.length}
								next={handleLoadMore}
								hasMore={registeredImages.length < totalRegisteredImagesCount}
								loader={
									<div key={0} className={globalClasses.loadMoreWrap}>
										<CopyrightAgentLoader />
									</div>
								}
								scrollableTarget="partnerImagesList"
							>
								{registeredImages.map((registeredImage, index) => (
									<Row justify="flex-start" key={index}>
										<div className={globalClasses.card}>
											<div
												style={{
													display: 'flex',
													justifyContent: 'space-between',
												}}
											>
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: '20px',
													}}
												>
													<Typography
														variant="body2"
														fontColor="rgb(142, 147, 155)"
													>
														Image:
													</Typography>
													<Typography variant="body2">
														{registeredImage.partner_image_id}
													</Typography>
												</div>
												<DangerButton
													onClick={() =>
														handleDeleteImage(
															registeredImage.registered_image_id,
														)
													}
													text={'Remove Image'}
												/>
											</div>
											<div className={globalClasses.rowDivider} />
										</div>
									</Row>
								))}
							</InfiniteScroll>
						) : (
							<div className={globalClasses.loadingContainer}>
								<Typography variant="body2" fontColor="rgb(142, 147, 155)">
									No images found.
								</Typography>
							</div>
						)}
					</div>
				</>
			) : (
				<div className={globalClasses.loadingContainer}>
					<div style={{ textAlign: 'center' }}>
						<Typography variant="body2">
							Registration Certificate could not be found.
						</Typography>
						<Typography variant="body2" fontColor="rgb(142, 147, 155)">
							Try to select another registration certificate to the left.
						</Typography>
					</div>
				</div>
			)}
		</div>
	)
}

export const NoRegistrationCertificateSelected = () => {
	const globalClasses = globalUseStyle()

	return (
		<div className={globalClasses.subPageContainer}>
			<div className={globalClasses.loadingContainer}>
				<div style={{ textAlign: 'center' }}>
					<Typography variant="body2">
						No Registration Certificate Selected
					</Typography>
					<Typography variant="body2" fontColor="rgb(142, 147, 155)">
						Select a registration certificate from the list to the left.
					</Typography>
				</div>
			</div>
		</div>
	)
}
